import React, { useState } from 'react';

const VISIBLE_TERM_LIMIT = 25;

function statusInfo(status, pass, closeness) {
  if (status === 'running') return { cls: 'running', label: 'Running...' };
  if (status === 'error') return { cls: 'error', label: 'Error' };
  if (status === 'pass' || pass === true) {
    return { cls: 'pass', label: closeness != null ? `PASS ${Math.round(closeness * 100)}%` : 'PASS' };
  }
  if (status === 'fail' || pass === false) {
    return { cls: 'fail', label: closeness != null ? `FAIL ${Math.round(closeness * 100)}%` : 'FAIL' };
  }
  return { cls: 'idle', label: 'Not run' };
}

function TermChips({ terms, label, emptyMsg }) {
  const [expanded, setExpanded] = useState(false);
  if (!terms || terms.length === 0) {
    return <p className="terms-empty">{emptyMsg}</p>;
  }
  const visible = expanded ? terms : terms.slice(0, VISIBLE_TERM_LIMIT);
  const hidden = terms.length - visible.length;
  return (
    <>
      <div className="term-chips" aria-label={label}>
        {visible.map((t, i) => (
          <span key={`${t.term || t.surface}-${i}`} className="term-chip" title={`weight ${t.weight}`}>
            {t.surface || t.term}
            {typeof t.weight === 'number' && (
              <span className="weight">{t.weight.toFixed(2)}</span>
            )}
          </span>
        ))}
      </div>
      {hidden > 0 && !expanded && (
        <button type="button" className="show-more-btn" onClick={() => setExpanded(true)}>
          + {hidden} more
        </button>
      )}
      {expanded && terms.length > VISIBLE_TERM_LIMIT && (
        <button type="button" className="show-more-btn" onClick={() => setExpanded(false)}>
          Show less
        </button>
      )}
    </>
  );
}

function TestCaseRow({
  testCase,
  index,
  status,
  result,
  expanded,
  onToggleExpand,
  onRun,
  onRunWithLlm,
  onEdit,
  onDelete,
  isAnyRunning,
}) {
  const closeness = result?.closeness ?? testCase.latest_run?.closeness ?? null;
  const pass = result?.pass ?? testCase.latest_run?.pass ?? null;
  // Bonus signal from the LLM judge (true when ACTUAL is more accurate /
  // detailed than EXPECTED). Auto-passes the test on the page side; here
  // we just decorate the closeness pill with "*" so the operator can see
  // why a low closeness still rendered as PASS.
  const exceedsExpected =
    result?.exceeds_expected ?? testCase.latest_run?.exceeds_expected ?? false;
  const actual = result?.actual_answer ?? testCase.latest_run?.actual_answer ?? '';
  const missing = result?.missing_terms ?? testCase.latest_run?.missing_terms ?? [];
  const extra = result?.extra_terms ?? testCase.latest_run?.extra_terms ?? [];
  const reasoning = result?.reasoning ?? testCase.latest_run?.llm_judge_reasoning ?? '';
  const scoringMethod = result?.scoring_method ?? testCase.latest_run?.scoring_method ?? '';
  const errorMsg = result?.error ?? testCase.latest_run?.error ?? '';
  const f1 = result?.f1 ?? testCase.latest_run?.f1;
  const bm25Norm = result?.bm25_normalized ?? testCase.latest_run?.bm25_normalized;
  const precision = result?.precision ?? testCase.latest_run?.precision;
  const recall = result?.recall ?? testCase.latest_run?.recall;

  const sb = statusInfo(status, pass, closeness);

  const wrapperCls = ['test-case-row'];
  if (sb.cls && sb.cls !== 'idle') wrapperCls.push(`is-${sb.cls}`);

  return (
    <div className={wrapperCls.join(' ')}>
      <div
        className="row-header"
        onClick={() => onToggleExpand(testCase.test_case_id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggleExpand(testCase.test_case_id); }}
      >
        <span className="row-index">{index + 1}.</span>
        <div className="row-title">
          <span>{testCase.title || `Test Case ${index + 1}`}</span>
          <span className="row-question-preview">{testCase.question}</span>
        </div>
        <span className={`status-badge ${sb.cls}`}>{sb.label}</span>
        <div className="closeness-bar with-label" aria-label="Closeness">
          <div className="closeness-bar" style={{ flex: 1 }}>
            <div
              className="closeness-fill"
              style={{ width: `${Math.max(0, Math.min(100, Math.round((closeness ?? 0) * 100)))}%` }}
            />
          </div>
          <span className="closeness-pct">
            {closeness != null ? `${Math.round(closeness * 100)}%` : '--'}
          </span>
        </div>
        <div className="row-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => onRun(testCase)}
            disabled={status === 'running' || isAnyRunning}
            title="Run this test case (BM25 scoring)"
          >
            Run
          </button>
          <button
            type="button"
            onClick={() => onRunWithLlm(testCase)}
            disabled={status === 'running' || isAnyRunning}
            title="Run + score with LLM judge"
          >
            LLM Judge
          </button>
          <button type="button" onClick={() => onEdit(testCase)} disabled={status === 'running'}>
            Edit
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => onDelete(testCase)}
            disabled={status === 'running'}
            title="Delete test case"
          >
            x
          </button>
        </div>
      </div>

      {expanded && (
        <div className="row-body">
          <div className="panel">
            <h4>Question</h4>
            <pre>{testCase.question}</pre>
          </div>
          <div className="panel">
            <h4>Expected Answer</h4>
            <pre>{testCase.expected_answer}</pre>
          </div>

          <div className={`panel ${actual ? '' : 'empty'}`} style={{ gridColumn: '1 / -1' }}>
            <h4>Rendered Answer{scoringMethod ? ` (${scoringMethod.toUpperCase()})` : ''}</h4>
            {actual ? <pre>{actual}</pre> : <span>Not yet run.</span>}
          </div>

          {errorMsg && (
            <div className="panel" style={{ gridColumn: '1 / -1', borderColor: '#fecaca', background: '#fef2f2' }}>
              <h4 style={{ color: '#b91c1c' }}>Error</h4>
              <pre style={{ color: '#991b1b' }}>{errorMsg}</pre>
            </div>
          )}

          {(closeness != null || f1 != null || bm25Norm != null) && (
            <div className="score-strip">
              {closeness != null && (
                <span
                  className="score-pill"
                  title={
                    exceedsExpected
                      ? 'Rendered answer exceeds expected (more accurate / detailed) -- counted as PASS'
                      : undefined
                  }
                >
                  Closeness <strong>{(closeness * 100).toFixed(1)}%{exceedsExpected ? '*' : ''}</strong>
                </span>
              )}
              {typeof f1 === 'number' && (
                <span className="score-pill">F1 <strong>{f1.toFixed(3)}</strong></span>
              )}
              {typeof precision === 'number' && (
                <span className="score-pill">Precision <strong>{precision.toFixed(3)}</strong></span>
              )}
              {typeof recall === 'number' && (
                <span className="score-pill">Recall <strong>{recall.toFixed(3)}</strong></span>
              )}
              {typeof bm25Norm === 'number' && (
                <span className="score-pill">BM25 <strong>{bm25Norm.toFixed(3)}</strong></span>
              )}
              <span className="score-pill">
                Threshold <strong>{Math.round((testCase.pass_threshold ?? 0.6) * 100)}%</strong>
              </span>
              {exceedsExpected && (
                <div className="exceeds-note">
                  * Rendered answer exceeds the expected answer (more accurate / detailed) -- counted as PASS.
                </div>
              )}
              {reasoning && <div className="reasoning"><strong>Judge:</strong> {reasoning}</div>}
            </div>
          )}

          <div className="terms-panel">
            <div className="terms-list missing">
              <h4>Missing terms (in expected, not in rendered)</h4>
              <TermChips
                terms={missing}
                label="Missing terms"
                emptyMsg={closeness != null ? 'No missing terms detected.' : 'Run the test to populate.'}
              />
            </div>
            <div className="terms-list extra">
              <h4>Extra terms (in rendered, not in expected)</h4>
              <TermChips
                terms={extra}
                label="Extra terms"
                emptyMsg={closeness != null ? 'No extra terms detected.' : 'Run the test to populate.'}
              />
            </div>
          </div>

          <div className="row-footer-actions">
            <span style={{ color: '#94a3b8', marginRight: 'auto' }}>
              {testCase.latest_run?.created_at && !result
                ? `Last run: ${new Date(testCase.latest_run.created_at).toLocaleString()}`
                : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestCaseRow;
